import React from "react";

import { useMutation, useQuery } from "@apollo/client";
import Container from "@material-ui/core/Container";
import AddIcon from "@material-ui/icons/Add";
import { useHistory, useParams } from "react-router-dom";

import RbActions from "components/RbActions";
import RbButtonAction from "components/RbButtonAction";
import RbFlexView from "components/RbFlexView";
import RbFlexViewItem from "components/RbFlexViewItem";
import RbHeader from "components/RbHeader";
import RbScrollView from "components/RbScrollView";
import useFields from "fields/hooks/useFields";
import useAlert from "hooks/useAlert";
import useMatchBreakpoints from "hooks/useMatchBreakpoints";
import useSnackbar from "hooks/useSnackbar";
import { convertValuesToInput } from "utils/graphqlUtils";

import ProductForm from "./ProductForm";
import {
  productQuery,
  createProductMutation,
  updateProductMutation,
  deleteProductMutation,
} from "./productGraphql";

function ProductScreen() {
  const history = useHistory();
  const params = useParams();
  const { openSnackbar } = useSnackbar();
  const { openAlert, closeAlert } = useAlert();
  const { fields, valid, handleChange, setValues, reset } = useFields();
  const actionsCompacted = useMatchBreakpoints({ xs: true, md: false });

  const { data: { product = {} } = {} } = useQuery(productQuery, {
    variables: {
      id: params.id,
    },
    onCompleted(data) {
      setValues(data.product);
    },
  });

  const [createProduct] = useMutation(createProductMutation, {
    onCompleted: (data) => {
      history.push(`/examples/products/${data.createProduct.id}`);
      openSnackbar({ message: "Product created successfully." });
    },
  });

  const [updateProduct] = useMutation(updateProductMutation, {
    onCompleted: () => {
      openSnackbar({ message: "Product updated successfully." });
    },
  });

  const [deleteProduct] = useMutation(deleteProductMutation, {
    onCompleted: () => {
      closeAlert();
      openSnackbar({ message: "Product deleted successfully." });
    },
  });

  const handleNew = React.useCallback(() => {
    reset();
    history.push("/examples/products/new");
  }, [history, reset]);

  const handleSave = React.useCallback(
    (values) => {
      const variables = {
        input: convertValuesToInput(values),
      };
      if (params.id === "new") {
        createProduct({ variables });
      } else {
        updateProduct({ variables });
      }
    },
    [createProduct, params.id, updateProduct]
  );

  const handleDelete = React.useCallback(() => {
    openAlert({
      title: "Do you really want delete?",
      message: "This action can't be undone.",
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Delete",
          color: "secondary",
          onClick: () => {
            deleteProduct({
              variables: {
                input: {
                  id: params.id,
                },
              },
            });
          },
        },
      ],
    });
  }, [deleteProduct, openAlert, params.id]);

  const newer = !params.id || params.id === "new";

  return (
    <RbFlexView>
      <RbFlexViewItem bgcolor="custom.high">
        <RbHeader
          title={newer ? "New product" : product?.name}
          subtitle={newer ? undefined : product?.manufacturer?.name}
          actions={
            <RbActions compacted={actionsCompacted}>
              <RbButtonAction Icon={AddIcon} onClick={handleNew} text="New" />
            </RbActions>
          }
        />
      </RbFlexViewItem>
      <RbFlexViewItem
        flex="1"
        bgcolor="custom.bg.100"
        borderTop={1}
        borderColor="custom.border.200"
      >
        <RbScrollView px={2} py={5}>
          <Container>
            <ProductForm
              fields={fields}
              onSubmit={handleSave}
              onDelete={handleDelete}
              onChange={handleChange}
              valid={valid}
              newer={newer}
            />
          </Container>
        </RbScrollView>
      </RbFlexViewItem>
    </RbFlexView>
  );
}

export default React.memo(ProductScreen);
